import { every, find, flatten, includes, toNumber, values } from "lodash";
import React, { useMemo } from "react";
import { useParams } from "react-router";
import ProfileHeader from "@/features/profile/components/ProfileHeader";
import { CompetencyMapSection } from "@/shared/widgets/CompetencyMapSection";
import { PersonalityMapSection } from "@/shared/widgets/PersonalityMapSection";
import { ProfileSummarySection } from "@/features/profile/components/ProfileSummarySection";
import { ProfileValuesSection } from "@/features/profile/components/ProfileValuesSection";
import { useDimensionsWithScores } from "@/features/people/hooks/useDimensionsWithScores";
import { HeaderContent } from "@/shared/widgets/HeaderContent";
import ProfileLayout from "@/features/profile/components/ProfileLayout";
import { usePeople } from "@/features/people/hooks/usePeople";
import ProfileSectionWrapper from "@/features/profile/components/ProfileLayout/ProfileSectionWrapper";
import { DataSection } from "@/shared/widgets/DataSection";
import { usePersonProfileData } from "@/features/people/hooks/usePersonProfileData";
import { IconButton } from "@/shared/ui/IconButton";
import { PDFExportTrigger } from "@/shared/pdf";
import { getGptFlowKey } from "@/shared/constants/gpt-flow-types";
import { usePersonNotes } from "@/features/people/hooks/usePersonNotes";
import {
  useProfileCompletion,
  useProfileLayoutProps,
} from "@/features/profile/hooks";
import { PersonProfilePdf } from "@/shared/pdf/Templates/Profiles";
import { AccessLevel } from "@/shared/constants";
import useViewerPermissions from "@/features/workspace/hooks/useViewerPermissions";
import usePerson from "@/features/people/hooks/usePerson";
import { ResourceEnum } from "@/shared/constants/resources";
import { CompetencyInsights } from "@/shared/widgets/InsightsSection/CompetencyInsights";
import { usePersonSurveyConfig } from "../hooks/usePersonSurveyConfigSelect";
import { ProfileEmptyState } from "@/features/profile";

function PersonProfileScreen() {
  const { orgId, memberId: _memberId } = useParams();

  const memberId = toNumber(_memberId);
  const { people } = usePeople({
    org_id: toNumber(orgId),
  });

  // TODO - needs business logic to determine survey config based on what dimensions the person has data for, and what surveys are configured for the organization
  // Or will we have a selector on the UI to select a survey config to use for the profile?
  const { config: surveyConfig } = usePersonSurveyConfig();

  const { dimensionsData, loading } = useDimensionsWithScores(
    {
      people: [memberId],
      type: surveyConfig,
    },
    people
  );

  const { validateAccessToResource } = useViewerPermissions();

  const hasPermissionsToEdit = useMemo(() => {
    return validateAccessToResource({
      resourceType: ResourceEnum.organization,
      accessLevel: AccessLevel.edit,
    });
  }, [validateAccessToResource, memberId]);

  const {
    person,
    loading: loadingPerson,
    handleUpsertPerson,
  } = usePerson(memberId);

  const { dataList } = usePersonProfileData({
    person,
    surveyConfig,
  });

  const hasCompletedAllSurveys = every(surveyConfig, (sc) =>
    includes(person?.completed_dim_types, sc)
  );

  const {
    notes,
    loading: loadingNotes,
    handleUpsertPersonNote,
    handleDeletePersonNote,
  } = usePersonNotes(memberId);

  const personSummary = find(notes, (n: any) => n.type === "summary");

  const { personalityComplete, competenciesComplete, valuesComplete } =
    useProfileCompletion(dimensionsData);

  const { layoutProps, tagsData, scope, loadingMessage } =
    useProfileLayoutProps({
      contextType: "person_profile",
      entity: person,
      dimensionsData,
      surveyConfig,
      people: [person],
      loadingStates: [loading, loadingPerson],
    });

  return (
    <>
      <HeaderContent>
        <PDFExportTrigger
          documentTitle={`${person.name} - Profile`}
          TriggerComponent={(props) => (
            <IconButton
              color="black"
              icon="Print"
              label="Print profile"
              variant="outline"
              {...props}
            />
          )}
          PDFComponent={
            <PersonProfilePdf
              dimensions={dimensionsData}
              person={person}
              summary={personSummary?.note || ""}
            />
          }
        />
        <></>
      </HeaderContent>
      <ProfileLayout {...layoutProps} loadingMessage={loadingMessage}>
        <ProfileHeader
          initials={person.initials || ""}
          name={person.name || ""}
          avatarUrl={person.avatar || ""}
          onContactChange={handleUpsertPerson}
          canEdit={hasPermissionsToEdit}
          email={person?.email || ""}
          phone={person?.phone || ""}
          linkedin={person?.linkedin || ""}
          isContact={!!person.is_contact}
        />
        <ProfileEmptyState
          variant="person"
          person={person}
          surveyConfig={surveyConfig}
          visible={!hasCompletedAllSurveys}
        />
        <DataSection
          title="Profile data"
          description="Manage your profile data"
          items={dataList}
        />
        <ProfileSectionWrapper label="summary" id="summary">
          <ProfileSummarySection
            onDeleteSummary={() =>
              handleDeletePersonNote(toNumber(personSummary?.id), "summary")
            }
            onSaveSummary={(text) =>
              handleUpsertPersonNote({
                note: text,
                type: "summary",
                id: toNumber(personSummary?.id),
              })
            }
            loading={loadingNotes}
            summaryContent={personSummary?.note}
            tagsData={tagsData}
            updatedAt={personSummary?.updated_at}
            scope={scope}
            flowId={getGptFlowKey("organization_member_profile_summary")}
          />
        </ProfileSectionWrapper>

        {personalityComplete && (
          <ProfileSectionWrapper label="personality" id="personality">
            <PersonalityMapSection
              dimensions={dimensionsData.personality}
              context="person"
            />
          </ProfileSectionWrapper>
        )}
        {valuesComplete && (
          <ProfileSectionWrapper label="values" id="values">
            <ProfileValuesSection
              dimensions={dimensionsData.values}
              context="person"
              defaultPersonId={memberId}
            />
          </ProfileSectionWrapper>
        )}
        {competenciesComplete && (
          <ProfileSectionWrapper label="competencies" id="competencies">
            <CompetencyMapSection
              dimensions={dimensionsData.competencies}
              context="person"
            />
            <CompetencyInsights
              context="person"
              person={person}
              dimensions={dimensionsData.competencies}
            />
          </ProfileSectionWrapper>
        )}
      </ProfileLayout>
    </>
  );
}

export default PersonProfileScreen;
